/**
 * EditableCatalogCell — wrapper inline-edit para campos del catalog.
 *
 * Política: el usuario admin/superadmin hace click en la celda para editar.
 * Enter o blur del input guardan vía PATCH /api/clients/:id/catalog/:idCatalogo.
 * Escape cancela. Mientras guarda muestra spinner. Tras guardar exitoso el
 * valor queda override local hasta que la data se refresque (la prox query
 * trae el valor actualizado de MariaDB).
 *
 * El backend setea automáticamente `manually_edited_fields` con el campo
 * editado, así el próximo batch LLM NO sobreescribe esta edición.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, X, Pencil } from 'lucide-react';
import { patchCatalogField } from '../../services/catalogAdmin';
import type { CatalogField } from './MergedTomaCatalogCell';

interface Props {
  row: any;
  clientId: string;
  field: CatalogField;
  /** Renderer del valor cuando NO está en edición (ej: MergedTomaCatalogCell). */
  display: React.ReactNode;
  /** Valor inicial del input — el resuelto por display logic (catalog o toma). */
  initialValue: string | null | undefined;
  /** uid del usuario actual — para el manually_edited_by audit. No es necesario porque el backend lo extrae del token. */
  disabled?: boolean;
}

export const EditableCatalogCell: React.FC<Props> = ({
  row,
  clientId,
  field,
  display,
  initialValue,
  disabled,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(initialValue == null ? '' : String(initialValue));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrideValue, setOverrideValue] = useState<string | null | undefined>(undefined);
  const [savedFlash, setSavedFlash] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // idCatalogo puede venir con distintas keys según la row
  const idCatalogo = row?.id_catalogo ?? row?.ID_CATALOGO ?? row?.id;

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus();
  }, [isEditing]);

  const enterEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || !idCatalogo) return;
    setDraft(overrideValue !== undefined ? (overrideValue ?? '') : (initialValue == null ? '' : String(initialValue)));
    setError(null);
    setIsEditing(true);
  }, [disabled, idCatalogo, initialValue, overrideValue]);

  const cancel = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const save = useCallback(async () => {
    if (!idCatalogo) return;
    const trimmed = draft.trim();
    const valueToSend = trimmed === '' ? null : trimmed;
    setSaving(true);
    setError(null);
    try {
      await patchCatalogField(clientId, String(idCatalogo), field, valueToSend);
      setOverrideValue(valueToSend);
      // Mutate row.original so otros cells del row (MergedTomaCatalogCell) reflejen el cambio.
      try {
        if (row) {
          row[field] = valueToSend;
          // Actualizar manually_edited_fields para que el merge muestre "(Manual)"
          let curList: string[] = [];
          const raw = row.manually_edited_fields;
          if (Array.isArray(raw)) curList = raw.map(String);
          else if (typeof raw === 'string') {
            try { const p = JSON.parse(raw); if (Array.isArray(p)) curList = p.map(String); } catch { /* noop */ }
          }
          if (!curList.includes(field)) curList.push(field);
          row.manually_edited_fields = curList;
        }
      } catch { /* noop */ }
      setIsEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [clientId, draft, field, idCatalogo, row]);

  if (isEditing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          onBlur={() => {
            // Pequeño delay para permitir click en los botones de save/cancel
            setTimeout(() => {
              if (isEditing && !saving) void save();
            }, 150);
          }}
          disabled={saving}
          className="w-full min-w-[110px] px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
          placeholder={`Editar ${field}`}
        />
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void save()}
          disabled={saving}
          className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-40"
          title="Guardar (Enter)"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          disabled={saving}
          className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
          title="Cancelar (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
        {error && <span className="text-[10px] text-red-600 ml-1" title={error}>!</span>}
      </div>
    );
  }

  // Modo display. Si hay override (post-save), mostrarlo como texto plain.
  // Caso contrario, usar el display renderer del caller (MergedTomaCatalogCell).
  return (
    <div
      className={`flex items-start justify-between group rounded px-1 py-0.5 transition-colors ${disabled ? '' : 'cursor-pointer hover:bg-gray-50'} ${savedFlash ? 'ring-1 ring-green-300 bg-green-50' : ''}`}
      onClick={enterEdit}
      title={disabled ? undefined : 'Click para editar'}
    >
      <span className="text-sm leading-tight">
        {overrideValue !== undefined ? (overrideValue ?? '—') : display}
      </span>
      {!disabled && (
        <Pencil className="w-3 h-3 ml-1 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
      )}
    </div>
  );
};

export default EditableCatalogCell;
